/**
 * Backend content block contracts — unified envelope introduced in Phase 1 of backend normalization.
 * All blocks arrive as type: "non_standard"; the discriminator is value.kind.
 */

import type { ActionState, EngineRunState } from "./engine-run-state.types";
import type { LayerContract } from "./evidence-layer.types";
import type { MapLayerContract } from "./map-layer.types";
import type { PlanData } from "./plan.types";

/** `value.kind` is the single discriminator of a content block — see content-blocks-PREVIEW-INGEST-UI-CONTRATO.md §1. */
export type ContentBlockKind =
  | "geometry"
  | "geolocation"
  | "place"
  | "geotiff"
  | "image"
  | "tabular"
  | "structured"
  | "layer_spec"
  | "message"
  | "blob"
  | "tool_frontend"
  | "background_job"
  | "plan"
  | "api_endpoint"
  | "map_layer";

/** The 10 kinds sharing `ContentBlockData` shape and a single generic card component. */
export const CONTENT_BLOCK_KINDS = new Set<ContentBlockKind>([
  "geometry",
  "geolocation",
  "place",
  "geotiff",
  "image",
  "tabular",
  "structured",
  "layer_spec",
  "message",
  "blob",
]);

export type BlockStatus = "pending" | "completed" | "error";

/** Matches backend ResultType vocabulary exactly — no translation needed. */
export type ResultType = "geojson" | "json" | "url" | "text" | "image" | "geotiff" | "pdf" | "binary";

// ─── Content block contract (content-blocks S1-S5, flat kind post-Fase 3) ─────

/** Format of the payload when `value` is inline (no file). */
export type ContentFormat = "image" | "geojson" | "geotiff" | "json" | "text" | "csv" | "binary";

/** File reference — present when the data lives in the file store (`ref !== null`, `value === null`). */
export interface ContentBlockRef {
  file_id: string;
  url: string;
  mime_type: string;
  size_bytes: number | null;
  filename: string;
  preview_url: string | null;
}

/**
 * Open dictionary keyed by `content_kind` — see ARCH doc §2.3 for the field table.
 * Can be `null` for blackboard evidence blocks without a projected PayloadMetadata yet
 * (backend D12) — every consumer must tolerate null and fall back to the generic card.
 */
export type ContentBlockMetadata = Record<string, unknown> | null;

/**
 * Open-ended string→string map raised by producers (`metadata.requested_params`,
 * `metadata.origin_stats`). The backend contract keeps every value as a string and
 * grows keys freely (any new producer stat appears without UI changes), so consumers
 * must iterate generically instead of listing keys.
 */
export type BlockStringMap = Record<string, string>;

/** Metadata read for display: scalars and flat primitive arrays stringify; objects/absent → null. */
export function getMetadataValue(metadata: ContentBlockMetadata, key: string): string | null {
  if (!metadata) return null;
  const raw = metadata[key];
  if (raw === null || raw === undefined || (typeof raw === "object" && !Array.isArray(raw))) return null;
  if (Array.isArray(raw)) {
    if (raw.some((item) => item === null || item === undefined || typeof item === "object")) return null;
    return raw.map((item) => String(item)).join(", ");
  }
  return String(raw);
}

/** Open-ended string map read from metadata; missing, empty or non-object → null (never render empty). */
export function getMetadataStringMap(metadata: ContentBlockMetadata, key: string): BlockStringMap | null {
  if (!metadata) return null;
  const raw = metadata[key];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const map: BlockStringMap = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v !== null && v !== undefined) map[k] = String(v);
  }
  return Object.keys(map).length > 0 ? map : null;
}

/** `requested_params` — what was requested (producing-call params; images also mix embedded XMP keys). */
export function getRequestedParams(metadata: ContentBlockMetadata): BlockStringMap | null {
  return getMetadataStringMap(metadata, "requested_params");
}

/** `origin_stats` — what happened at the origin (fetch_latency_ms, response_content_type, pagination_*…). */
export function getOriginStats(metadata: ContentBlockMetadata): BlockStringMap | null {
  return getMetadataStringMap(metadata, "origin_stats");
}

/** `capture_metadata` — imagery capture facts from the embedded XMP packet (open key set). */
export function getCaptureMetadata(metadata: ContentBlockMetadata): BlockStringMap | null {
  return getMetadataStringMap(metadata, "capture_metadata");
}

/**
 * Humanizes an open-ended map key for display (S9 rule 3): underscores become spaces and the
 * result is trimmed — no translation, no case games, so unknown future keys read naturally.
 */
export function humanizeMapLabel(key: string): string {
  return key.replace(/_/g, " ").trim();
}

/**
 * Client-side display formatting for one string stat value (S9 rule 2: all origin stat
 * values are STRINGS, parsed here for display only). Keys ending in `_ms` whose value
 * parses as a finite non-negative number render as milliseconds under one second
 * ("850 ms") and as seconds with one decimal from one second up ("4200" → "4.2 s").
 * Anything else — non-`_ms` keys, unparsable/negative values — passes through unchanged,
 * so unknown future stats stay readable as-is.
 */
export function formatStatValue(key: string, value: string): string {
  if (!key.endsWith("_ms")) return value;
  if (value.trim() === "") return value;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return value;
  if (parsed < 1000) return `${parsed} ms`;
  return `${(parsed / 1000).toFixed(1)} s`;
}

/**
 * Normalizes a block `ref`: the backend may deliver blob refs as a plain URL string instead
 * of the full ref object. A string becomes a minimal ref carrying only the URL so consumers
 * keep a single shape; object refs pass through unchanged.
 */
export function resolveBlockRef(ref: ContentBlockRef | string | null): ContentBlockRef | null {
  if (ref === null) return null;
  if (typeof ref === "string") {
    return { file_id: "", url: ref, mime_type: "", size_bytes: null, filename: "", preview_url: null };
  }
  return ref;
}

/**
 * Traceability ids correlating the block with its plan task/attempt (S9): `task_id`,
 * `attempt_id` and `capability_id` render as one small muted debug-only line when
 * present; any other keys (evidence_id, tool_name) stay debug/logging-only.
 */
export type ContentBlockSource = Record<string, unknown>;

/** Kinds that carry `metadata.bbox`/`metadata.center` usable for quick map actions. */
export const GEO_CONTENT_KINDS = new Set<ContentBlockKind>(["geometry", "geolocation", "place", "map_layer"]);

/**
 * Kinds rendered as an inline chip (fila aparte, fuera del grid) instead of a grid card — see
 * content-blocks-PREVIEW-INGEST-UI-IMPLEMENTACION.md §1/§2.
 */
export const CHIP_CONTENT_KINDS = new Set<ContentBlockKind>(["geolocation", "place"]);

/**
 * Maps a MIME type to the nearest ContentFormat for rendering.
 * Returns "binary" for unrecognised MIME types.
 */
export function mimeToContentFormat(mimeType: string): ContentFormat {
  if (mimeType === "image/jpeg" || mimeType === "image/png") return "image";
  if (mimeType === "application/geo+json") return "geojson";
  if (mimeType === "image/tiff") return "geotiff";
  if (mimeType === "application/json") return "json";
  if (mimeType === "text/csv") return "csv";
  if (mimeType === "text/plain") return "text";
  return "binary";
}

/** Maps the network-facing ContentFormat to the frontend's internal ResultType rendering vocabulary. */
export function contentFormatToResultType(format: ContentFormat): ResultType {
  if (format === "csv") return "text";
  return format;
}

export interface ContentBlockData {
  format: ContentFormat;
  label: string;
  /** Invariant: `ref !== null` XOR `value !== null` — never both, never neither. */
  ref: ContentBlockRef | null;
  value: unknown | null;
  metadata: ContentBlockMetadata;
  source: ContentBlockSource;
  /**
   * Optional `data.layer` annotation of the evidence-layer contract (evidence-layer-ui US1,
   * backend evidence-layer-contract S1): the grouped `{ type, plan_ref, entity, label }` object
   * the backend attaches to mappable geometry/geolocation evidence. Present only when the
   * backend annotates the block — the key is omitted entirely otherwise, so pre-contract
   * blocks keep a byte-identical envelope. Resolve it through `resolveLayerContract`
   * (evidence-layer.types.ts) instead of reading this field raw.
   */
  layer?: LayerContract;
  /**
   * Optional grouped `data.map_layer` contract of promoted map-layer evidence
   * (map-layer-block-ui S2): `{ layer_id, name, style, style_metadata }`. `layer_id` present →
   * the UI offers UPDATE of that existing layer; null → the UI offers ADD as a new layer
   * (S2's exact update-vs-add rule — no other field participates). Present only when the
   * backend emits the map_layer block — the key is omitted entirely otherwise, so blocks
   * without it keep a byte-identical envelope. Resolve it through `resolveMapLayerContract`
   * (map-layer.types.ts) instead of reading this field raw. Deliberately NOT `data.layer`:
   * map_layer is out of the geometry `_LAYER_KINDS` router (agent-deployment-api
   * `evidence_blocks.py:309-316`).
   */
  map_layer?: MapLayerContract;
}

/**
 * status "pending"   = missing_required is non-empty, URL contains <placeholder> tokens.
 * status "completed" = all params resolved, URL is executable.
 */
export interface ApiEndpointData {
  operation_id: string;
  method: string;
  methods: string[];
  url: string;
  description: string;
  /** @deprecated backend no longer emits this (content-blocks S5 §1.3) — do not read. */
  response_type?: ResultType;
  resolved_params?: Record<string, unknown>;
  missing_required?: Array<{ name: string; type: string; description: string }>;
}

/**
 * Backend emits status "pending". Frontend executes the action and updates
 * status locally (signal on the component) to "completed" | "error".
 */
export interface ToolFrontendData {
  tool_name: string;
  args: Record<string, unknown>;
}

// ─── Background Job types (mirrored from shared/async_jobs/models.py) ─────────

export type JobStatus = "pending" | "running" | "completed" | "error" | "cancelled" | "skipped";

/**
 * One historical, terminal-only step result persisted inside `PhaseDetail.steps`. Same shape as
 * the live `pipeline_phase_step` CUSTOM event — reuse whatever parses that event for this too.
 * Only ever `completed`/`error` in practice: "running" is never persisted, it only exists live via SSE.
 */
export interface StepResult {
  phase: string;
  step: string;
  label: string;
  status: JobStatus;
  message: string | null;
}

export interface PhaseDetail {
  name: string;
  label: string;
  status: JobStatus;
  started_at: string | null;
  completed_at: string | null;
  record_count: number | null;
  error_message: string | null;
  /**
   * Append-only history of step results for phases with sub-steps (`discovery`, `report`).
   * Empty for `enrich`/`deep_analysis` (per-record/sub-agent progress, no step breakdown).
   * Persisted from job_progress — survives reload even when no SSE connection is live.
   */
  steps: StepResult[];
}

export interface JobProgress {
  status: JobStatus;
  current_phase: string | null;
  phases_total: number;
  phases_completed: number;
  started_at: string | null;
  completed_at: string | null;
  phases: PhaseDetail[];
}

/** A single provider selected by the planner for a pipeline phase. */
export interface ProviderConfig {
  name: string;
  confidence?: number;
  reasoning?: string;
  layer?: string;
}

export interface PhaseStats {
  total: number;
  ok: number;
  failed: number;
  filtered: number;
  duration_ms: number;
  provider: string;
}

export interface PipelineSummary {
  total_records: number;
  phases_completed: string[];
  total_duration_ms: number;
  output_types: string[];
  errors_by_phase: Record<string, number>;
  enrich_stats?: PhaseStats;
}

export interface OutputRef {
  ref_id: string;
  filename: string;
  url: string | null;
  content_type: string | null;
  content_category: string | null;
  /** Semantic role of the file within a pipeline run (e.g. "vlm_geojson", "properties_geojson"). */
  output_type?: string;
}

export interface ResolvedToolCall {
  id: string;
  name: string;
  hasResult: boolean;
}

/**
 * How a live job's progress should be tracked: `sse` (stream only), `polling` (getThreadState on
 * a timer only — the option that avoids depending on the SSE connection reaching the same pod
 * that owns the run in a multi-pod backend), or `both` at once. Backend-controlled per job/
 * task_type via `BackgroundJobBlockData.tracking_mode`; when unset the frontend applies its own
 * fleet-wide default (see `DEFAULT_TRACKING_MODE` in background-job-tracker.service.ts).
 */
export type BackgroundJobTrackingMode = "sse" | "polling" | "both";

/**
 * Static payload embedded in the AIMessage content block.
 * Only `job_id` is guaranteed across the job's lifecycle. While the job is live, the
 * routing/display fields below are present. Once the backend applies its completion
 * patch, `data` is replaced with a reduced shape carrying only `job_id`, `job_progress`,
 * `total_records` and `pipeline_summary` — the routing fields are gone since polling is
 * no longer needed for a finished job.
 */
export interface BackgroundJobBlockData {
  job_id: string;
  job_thread_id?: string;
  job_run_id?: string;
  assistant_id?: string;
  task_type?: string;
  display_name?: string;
  expected_phases?: string[];
  expected_phase_labels?: Record<string, string>;
  poll_interval_ms?: number;
  /** Optional per-job/task_type override of how live progress is tracked. See `BackgroundJobTrackingMode`. */
  tracking_mode?: BackgroundJobTrackingMode;
  /** Present once the backend completion patch has replaced `data` (job already terminal). */
  job_progress?: JobProgress;
  /** Added by the backend completion patch — present after the job finishes. */
  total_records?: number;
  /** Added by the backend completion patch — present after the job finishes. */
  pipeline_summary?: PipelineSummary;
}

/** Combined snapshot consumed by components — static block data + dynamic polling state. */
export interface BackgroundJobSnapshot {
  block: BackgroundJobBlockData;
  progress: JobProgress | null;
  output_refs: OutputRef[];
  /**
   * null  — first poll not yet returned.
   * pending / running / interrupted — active, non-terminal (keep polling).
   * success / error / timeout — terminal (stop polling).
   */
  run_status: "pending" | "running" | "interrupted" | "success" | "error" | "timeout" | null;
  /** Present when run_status === 'error' and the failure originated outside of a phase (e.g. infra crash). */
  run_error_message: string | null;
  /** Pipeline planner fields extracted from checkpoint state for debug inspection (debug mode only). Present once providers key appears in stateValues. */
  state_debug?: Record<string, unknown>;
  /**
   * Live TODO checklist for the job's sub-agent, from any `*_todo_update` CUSTOM event (see
   * `isTodoUpdateEvent`) — e.g. `deep_analysis_todo_update`. Backend sends the full current
   * list each time — replaced wholesale.
   */
  subAgentTodos: SubAgentTodo[];
  /**
   * Human-readable name of the sub-agent driving `subAgentTodos`/`deepAnalysis.toolCall`,
   * derived from the `*_todo_update`/`*_tool_call` event name (see `deriveSubAgentLabel`) —
   * e.g. "Deep Analysis". Null before the first such event arrives.
   */
  subAgentLabel: string | null;
  /**
   * Live per-phase status + message from `pipeline_phase_status` CUSTOM events, keyed by
   * phase name — dispatched by `_start_phase`/`_finish_phase`/`_error_phase` (backend
   * phase_progress.py). This is the AUTHORITATIVE live phase status: `job_progress`
   * (from STATE_SNAPSHOT, see `progress.phases`) only reflects pending/final state —
   * mid-run STATE_SNAPSHOTs don't carry per-phase running/completed transitions anymore,
   * that's now this channel's job. TaskListCardComponent reads this (falling back to
   * `progress.phases[].status`) to decide each phase row's icon/spinner and to show the
   * running-phase detail block (message/steps/deep_analysis/todos).
   */
  phaseStates: Record<string, PipelinePhaseState>;
  /**
   * Internal step progress for phases with sub-steps (`discovery`'s 9 graph nodes,
   * `report`'s 2), from `pipeline_phase_step` CUSTOM events — not sent for `enrich`
   * (per-record loop, would flood). Upserted by `step` key, keyed by phase name.
   */
  phaseSteps: Record<string, PipelinePhaseStep[]>;
  /**
   * Live activity for the deep_analysis sub-agent — tool calls and streamed response
   * text. Both channels are implicitly scoped to deep_analysis: it's the only isolated
   * sub-agent using `deep_analysis_tool_call`/`deep_analysis_message_chunk`, so no
   * phase/source field is needed on the events themselves.
   */
  deepAnalysis: DeepAnalysisActivity;
}

export interface SubAgentTodo {
  content: string;
  status: "pending" | "in_progress" | "completed";
}

/** True once every todo in the list is completed — used to collapse/hide a finished checklist. */
export function isSubAgentTodoListDone(todos: SubAgentTodo[]): boolean {
  return todos.length > 0 && todos.every((t) => t.status === "completed");
}

/**
 * True for any sub-agent's TODO-checklist CUSTOM event — `deep_analysis_todo_update`,
 * `style_todo_update`, and any future `write_todos`-backed sub-agent's own `*_todo_update`
 * event. Every one of them carries an identical `{ todos: SubAgentTodo[] }` payload (full list,
 * replace-wholesale semantics) — only the event name, and thus which sub-agent produced it,
 * differs. Matched by suffix so a new sub-agent's todo event needs no code change here.
 */
export function isTodoUpdateEvent(eventName: string): boolean {
  return eventName.endsWith("_todo_update");
}

/**
 * True for any sub-agent's already-completed tool-call CUSTOM event — `deep_analysis_tool_call`
 * (pipeline, handled by `BackgroundJobTrackerService`), `style_tool_call` (plain conversational
 * turn, handled by `WorkerTodoStreamService.storeToolCall`), and any future sub-agent's own
 * `*_tool_call` event. Payload is always `{ tool_name: str, label: str }` and fires AFTER the
 * tool already completed — rendered as an activity row above the todo checklist, same layout as
 * `TaskListCardComponent`'s pipeline equivalent. Matched by suffix so a new sub-agent's tool-call
 * event needs no code change here.
 */
export function isSubAgentToolCallEvent(eventName: string): boolean {
  return eventName.endsWith("_tool_call");
}

/**
 * Derives a human-readable sub-agent/task name from a `*_todo_update` or `*_tool_call` CUSTOM
 * event name — `style_todo_update` → "Style", `deep_analysis_tool_call` → "Deep Analysis". Used
 * as the todo checklist's card title in both flows: `WorkerTodoStreamService` (plain
 * conversational turn) and `BackgroundJobTrackerService` (pipeline, via `subAgentLabel` on
 * `BackgroundJobSnapshot`) — same event-name contract, so the same derivation applies to both
 * without a per-agent name lookup table.
 */
export function deriveSubAgentLabel(eventName: string): string {
  const prefix = eventName.replace(/_todo_update$|_tool_call$/, "");
  return prefix
    .split("_")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");
}

export type PipelinePhaseEventStatus = "running" | "completed" | "warning" | "error" | "skipped";

export interface PipelinePhaseState {
  status: PipelinePhaseEventStatus;
  message: string | null;
}

export interface PipelinePhaseStep {
  /** Stable identity within a phase, e.g. "coverage_filter". */
  step: string;
  label: string;
  status: PipelinePhaseEventStatus;
  message: string | null;
}

export interface DeepAnalysisActivity {
  /** Last completed tool call — ephemeral, replaced wholesale by the next call. */
  toolCall: { name: string; label: string } | null;
  /** Accumulated text of `deep_analysis_message_chunk` deltas for the current turn. */
  message: string;
}

// ─── Plan model (contract doc 31 — raw Plan aggregate) ───────────────────────

/**
 * The `plan` content block carries the RAW contract Plan aggregate (`Plan.model_dump(mode="json")`,
 * doc 31 §4.2).
 *
 * `PlanData` is re-exported here so block consumers keep a single import site (the block parser,
 * the content dispatcher and the workspace-index debug card import the plan model from this
 * file); the canonical home of the plan model stays `./plan.types`.
 */
export type { PlanData };

// ─── Blackboard snapshot (planning pipeline progress, CUSTOM event, doc 31 §7) ─

/**
 * The 8 `PhaseId` values of the `blackboard_snapshot` SSE catalog, in lifecycle order —
 * frozen frontend contract (doc 31 §2.3/§7.2).
 */
export type BlackboardPhase =
  | "plan_router"
  | "semantic_decomposition"
  | "capability_resolution"
  | "provider_binding"
  | "plan_triage"
  | "plan_assessment"
  | "plan_run"
  | "run_engine";

/**
 * Sub-step within the topology of a phase (doc 31 §7.2): `generate`/`judge`/`correct` for the
 * F1-F3 phases (`semantic_decomposition`/`capability_resolution`/`provider_binding`, with
 * `attempt` on judge/correct), `replan` for `plan_triage`, `plan_open`/`plan_profile`/`task_facts`/
 * `review` for `plan_assessment`, `plan_prepare`/`plan_close` for `plan_run`. Always null for
 * `plan_router` and `run_engine`.
 */
export type BlackboardStep =
  | "generate"
  | "judge"
  | "correct"
  | "replan"
  | "plan_open"
  | "plan_profile"
  // plan_assessment emits task_facts running/completed on both exits (doc 31 §7.2, SSE v2).
  | "task_facts"
  | "review"
  | "plan_prepare"
  | "plan_close";

/** Live status for one phase indicator slot — overwritten in place per `blackboard_snapshot` event (doc 31 §7.1). */
export interface BlackboardIndicator {
  status: string;
  step: BlackboardStep | null;
  attempt: number | null;
}

/**
 * One entry of the LIVE planning-progress list carried by `blackboard_snapshot.tasks` (SSE v2,
 * backend `plan_tasks_payload`). Elements may carry additional fields the frontend ignores —
 * only `task_id`/`intent` are read (defensively guarded at runtime, never thrown on).
 */
export interface BlackboardPlanningTask {
  task_id: string;
  /** `Task.intent` — the human label surfaced while the engine has not started. */
  intent: string;
}

/**
 * Per-task row of the live engine board (doc 31 §7.3) — keyed by task_id, not phase. `state`
 * is the raw `ActionState` value of the task (full `plan_run_state` dumps, doc 31 §7.4),
 * lowercase `"running"` (SSE v2) as the TRANSIENT attempt-start state, or `"WAITING"` — the
 * collapsed state synthesized from the status contract v3 task-level `waiting` diff (the wire
 * no longer distinguishes the park variants; a dump-carried parked task keeps its raw
 * variant). `attempts` is the COUNT of executed attempts (`TaskExecution.attempts.length`),
 * not the attempts array.
 */
export interface BlackboardTaskRow {
  task_id: string;
  state: ActionState | "running" | "WAITING";
  attempts: number;
}

/**
 * Frontend-side memory of the live attempt line (SSE v2): the task currently executing, its
 * intent (when the attempt-start event carried one), the resolver id and the 1-based attempt
 * counter. Null while no attempt is live — a heartbeat never clears it; the next full
 * EngineRunState snapshot or `clear()` does.
 */
export interface BlackboardRunAttempt {
  task_id: string;
  /** `task_description` (Task.intent) of the attempt-start event; null when absent. */
  description: string | null;
  /** Catalog id executing the attempt; null when the event carried none. */
  resolver_id: string | null;
  /** 1-based attempt counter of the attempt-start event (defaults to 1 when absent). */
  attempt: number;
}

/**
 * Frontend-side memory of the live park/wait reason (SSE v2): `PENDING`+detail ·
 * `WAITING_INPUT`+detail. Null while not parked; cleared by the next attempt-start or full
 * EngineRunState snapshot (fresh truth).
 */
export interface BlackboardRunWait {
  /** Raw `ActionState` word that parked the run (`"PENDING"` | `"WAITING_INPUT"`), or the collapsed `"WAITING"` of the status contract v3 when the wire variant is unknowable. */
  state: ActionState | "WAITING";
  /** `pending_reason.kind` (+ required kinds when present) of the park event. */
  detail: string;
}

/**
 * Raw `blackboard_snapshot` CUSTOM-event payload sent by the backend — exactly 11 keys (doc 31
 * §7.1, SSE v2; 5 new optional keys, null when N/A). `status` carries the generic
 * `"running" | "completed" | "error"` vocabulary for regular phases, but for `run_engine` it
 * depends on the event level (doc 31 §7.3): a TASK-level event (`task_id !== null`) carries the
 * raw `ActionState` value of the changed task — with the transient lowercase `"running"` on
 * attempt-start — while an ENGINE-level event (`task_id === null && phase === "run_engine"`,
 * tick heartbeat and per-pass progress/completion) carries the lowercase engine vocabulary
 * (`"running"`/`"completed"`). `task_id` is null in every other phase. `plan_run_state` carries
 * the raw EngineRunState dump ONLY with `phase === "run_engine"` on a terminal task transition
 * or HITL suspension change; intermediate transitions carry null and must NEVER clear stored
 * state (doc 31 §7.4 null-keep rule).
 *
 * New shapes (SSE v2): tick heartbeat (`run_engine`/`running`, no `task_id` = pure liveness);
 * attempt-start (`running` + `task_id` + 1-based `attempt` + `resolver_id` +
 * `task_description`); park/blocked (`PENDING`+`detail` · `WAITING_INPUT`+`detail`); engine
 * pass progress (`run_engine` engine-level events naming the pass in `engine_pass`, whose
 * `completed` fires only when the whole engine run finished that pass); terminal dump
 * unchanged.
 */
export interface BlackboardSnapshotEvent {
  phase: BlackboardPhase;
  /**
   * Event status — status contract v3 closed vocabulary: `"running" | "completed" | "error" |
   * "waiting"`. Task-level events (`task_id != null`): attempt-start/heartbeat `running`,
   * terminal `completed`/`error`, parks `waiting` (+ `detail`). Engine-level events
   * (`task_id === null`, `phase === "run_engine"`): `running`/`completed` (`error` tolerated
   * as failed). The raw ActionState words are no longer emitted; they are tolerated verbatim
   * (legacy/out-of-order deployments) and unknown strings are logged, never assumed.
   */
  status: string;
  step: BlackboardStep | null;
  attempt: number | null;
  task_id: string | null;
  plan_run_state: EngineRunState | null;
  /** `Task.intent` of the named task (attempt-start) — the label-join fallback for the board. */
  task_description?: string | null;
  /**
   * LIVE planning-progress list — populated on planning-phase events (F2/F3 completed,
   * review/plan_triage running). Nuances: (1) during an F1 review the running events carry the
   * CURRENT revision's tasks (it is the current plan's list → replace the stored map
   * wholesale, last wins); (2) the `_generate` running event carries NO tasks — the preceding
   * completed already delivered them → null must NEVER clear the stored map (null-keep, same
   * rule as `plan_run_state`).
   */
  tasks?: BlackboardPlanningTask[] | null;
  /** `pending_reason.kind` (+ required kinds when present) on park/blocked events. */
  detail?: string | null;
  /** Catalog id executing the attempt (attempt-start). */
  resolver_id?: string | null;
  /**
   * Engine pass currently running or just finished (engine-pass contract): `"discovery" |
   * "execution" | "analysis"` while that pass runs; null on non-engine events and on
   * engine-level events of passes without a policy (those group under a neutral fallback
   * row). Unknown future strings must be tolerated, never assumed.
   */
  engine_pass?: string | null;
}

// ─── Raw backend envelope ─────────────────────────────────────────────────────

export interface UnifiedBlock {
  type: "non_standard";
  value: {
    kind: ContentBlockKind;
    id: string;
    status: BlockStatus;
    data: ContentBlockData | ApiEndpointData | ToolFrontendData | BackgroundJobBlockData | PlanData;
  };
}

// ─── Normalized frontend union (post-parse, type-safe) ───────────────────────

export type NormalizedContentBlock =
  | {
      kind: Exclude<ContentBlockKind, "tool_frontend" | "background_job" | "plan" | "api_endpoint">;
      id: string;
      status: BlockStatus;
      data: ContentBlockData;
    }
  | { kind: "api_endpoint"; id: string; status: BlockStatus; data: ApiEndpointData }
  | { kind: "tool_frontend"; id: string; status: BlockStatus; data: ToolFrontendData }
  | { kind: "background_job"; id: string; status: BlockStatus; data: BackgroundJobBlockData }
  | { kind: "plan"; id: string; status: BlockStatus; data: PlanData };

// ─── Render config ────────────────────────────────────────────────────────────

export type RenderMode = "silent" | "inline" | "card";
export type PlacementType = "inline" | "grid";

export interface BlockRenderConfig {
  renderMode: RenderMode;
  /** Only meaningful when renderMode !== "silent". */
  placementType: PlacementType;
  /** When true, block is only rendered if DebugModeService.isDebugMode() === true. */
  debugOnly: boolean;
}
